import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import {
  ADD_RD_PROJECTS_FILE,
  AddRdProjectsFileMutation,
  AddRdProjectsFileMutationVariables,
  GET_BACKGROUND_OPERATIONS,
  GET_COMPANY,
  GET_ONE_SCHEMA_JWT,
  GET_RD_PROJECTS_FILE_SIGNED_UPLOAD_URL,
  GetBackgroundOperationsQueryVariables,
  GetCompanyQueryVariables,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  GetRdProjectsFileSignedUploadUrlMutation,
  GetRdProjectsFileSignedUploadUrlMutationVariables,
  useCurrentUser,
} from '@neo.tax/shared';

type UseAddRdProjectsFileParams = {
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
};

type UseAddRdProjectsFileReturn = {
  handleSuccess: () => Promise<void>;
  isLoading: boolean;
  oneSchemaJwt: string | null;
  permanentUrl: string | null;
  signedUrl: string | null;
};

const BULK_UPLOAD_FILE_NAME = 'rd-projects-bulk-create.csv';

export const useAddRdProjectsFile = ({
  companyId,
  fiscalYearId,
  isOpen,
}: UseAddRdProjectsFileParams): UseAddRdProjectsFileReturn => {
  const { user } = useCurrentUser();
  const [getOneSchemaJwt, { data: oneSchemaJwtData }] = useMutation<
    GetOneSchemaJwtMutation,
    GetOneSchemaJwtMutationVariables
  >(GET_ONE_SCHEMA_JWT);
  const [getUploadUrlMutation, { data: uploadUrlData, loading: isLoading }] = useMutation<
    GetRdProjectsFileSignedUploadUrlMutation,
    GetRdProjectsFileSignedUploadUrlMutationVariables
  >(GET_RD_PROJECTS_FILE_SIGNED_UPLOAD_URL);
  const [addRdProjectsFile] = useMutation<AddRdProjectsFileMutation, AddRdProjectsFileMutationVariables>(
    ADD_RD_PROJECTS_FILE,
  );

  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      if (!isOpen || !user?.id) {
        return;
      }
      await getOneSchemaJwt({
        variables: { userId: user.id },
        context: {
          errorToast: { title: 'Error preparing bulk upload' },
        },
      });
    };
    initOneSchemaJwt();
  }, [getOneSchemaJwt, isOpen, user?.id]);

  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      if (!isOpen) {
        return;
      }
      await getUploadUrlMutation({
        variables: { fiscalYearId, fileName: BULK_UPLOAD_FILE_NAME },
        context: {
          errorToast: { title: 'Error preparing bulk upload' },
        },
      });
    };
    initUploadUrls();
  }, [fiscalYearId, getUploadUrlMutation, isOpen]);

  const handleSuccess = useCallback(async (): Promise<void> => {
    const permanentUrl = uploadUrlData?.getRdProjectsFileSignedUploadUrl.permanentUrl;
    if (!permanentUrl) {
      return;
    }
    await addRdProjectsFile({
      variables: {
        fiscalYearId,
        input: {
          permanentUrl,
        },
      },
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { companyId } satisfies GetCompanyQueryVariables,
        },
        {
          query: GET_BACKGROUND_OPERATIONS,
          variables: { companyId } satisfies GetBackgroundOperationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        successToast: {
          title: 'Bulk upload started!',
          description: "We're creating your projects. They'll appear on this page once processing completes.",
        },
        errorToast: { title: 'Failed to start bulk upload' },
      },
    });
  }, [addRdProjectsFile, companyId, fiscalYearId, uploadUrlData]);

  return {
    handleSuccess,
    isLoading,
    oneSchemaJwt: oneSchemaJwtData?.getOneSchemaJwt ?? null,
    permanentUrl: uploadUrlData?.getRdProjectsFileSignedUploadUrl.permanentUrl ?? null,
    signedUrl: uploadUrlData?.getRdProjectsFileSignedUploadUrl.signedUrl ?? null,
  };
};
